/**
 * Responsive Theme Configuration
 * Task 14.1b: Full screen responsive support
 * Requirements: 8.1, 8.2
 *
 * Defines breakpoints and responsive utilities for:
 * - Mobile (320px+)
 * - Tablet (768px+)
 * - Desktop (1024px+)
 */
import { createTheme } from '@mui/material/styles';
import type { ThemeOptions, Components, Theme } from '@mui/material/styles';

// ============================================================
// Breakpoint Configuration
// ============================================================

/**
 * Custom breakpoints matching design requirements
 * - xs: 0-319px (small mobile)
 * - sm: 320-767px (mobile)
 * - md: 768-1023px (tablet)
 * - lg: 1024-1439px (desktop)
 * - xl: 1440px+ (large desktop)
 */
export const breakpoints = {
  values: {
    xs: 0,
    sm: 320,
    md: 768,
    lg: 1024,
    xl: 1440,
  },
};

// ============================================================
// Spacing Configuration
// ============================================================

/**
 * Responsive spacing values
 */
export const spacing = {
  mobile: {
    page: 2, // 16px
    section: 2,
    element: 1,
  },
  tablet: {
    page: 3, // 24px
    section: 3,
    element: 2,
  },
  desktop: {
    page: 4, // 32px
    section: 4,
    element: 2,
  },
};

// ============================================================
// Typography Configuration
// ============================================================

/**
 * Responsive typography scales
 */
export const responsiveTypography: ThemeOptions['typography'] = {
  fontFamily: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
    '"Noto Sans JP"',
  ].join(','),
  h1: {
    fontSize: '2rem',
    fontWeight: 700,
    '@media (min-width:768px)': {
      fontSize: '2.5rem',
    },
    '@media (min-width:1024px)': {
      fontSize: '3rem',
    },
  },
  h2: {
    fontSize: '1.5rem',
    fontWeight: 600,
    '@media (min-width:768px)': {
      fontSize: '1.875rem',
    },
    '@media (min-width:1024px)': {
      fontSize: '2.25rem',
    },
  },
  h3: {
    fontSize: '1.25rem',
    fontWeight: 600,
    '@media (min-width:768px)': {
      fontSize: '1.5rem',
    },
    '@media (min-width:1024px)': {
      fontSize: '1.75rem',
    },
  },
  h4: {
    fontSize: '1.125rem',
    fontWeight: 600,
    '@media (min-width:768px)': {
      fontSize: '1.25rem',
    },
    '@media (min-width:1024px)': {
      fontSize: '1.5rem',
    },
  },
  h5: {
    fontSize: '1rem',
    fontWeight: 600,
    '@media (min-width:768px)': {
      fontSize: '1.125rem',
    },
  },
  h6: {
    fontSize: '0.875rem',
    fontWeight: 600,
    '@media (min-width:768px)': {
      fontSize: '1rem',
    },
  },
  body1: {
    fontSize: '0.875rem',
    '@media (min-width:768px)': {
      fontSize: '1rem',
    },
  },
  body2: {
    fontSize: '0.75rem',
    '@media (min-width:768px)': {
      fontSize: '0.875rem',
    },
  },
  button: {
    fontSize: '0.875rem',
    fontWeight: 500,
    textTransform: 'none',
  },
};

// ============================================================
// Component Overrides
// ============================================================

/**
 * Responsive component overrides
 */
export const responsiveComponents: Components<Theme> = {
  MuiButton: {
    styleOverrides: {
      root: {
        minHeight: 44, // Minimum touch target size (44px)
        minWidth: 44,
        padding: '10px 16px',
        '@media (min-width:768px)': {
          minHeight: 40,
          padding: '8px 16px',
        },
      },
      sizeLarge: {
        minHeight: 48,
        padding: '12px 24px',
        fontSize: '1rem',
      },
      sizeSmall: {
        minHeight: 36,
        padding: '6px 12px',
        fontSize: '0.8125rem',
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        minHeight: 44,
        minWidth: 44,
        '@media (min-width:768px)': {
          minHeight: 40,
          minWidth: 40,
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiInputBase-root': {
          minHeight: 44,
          '@media (min-width:768px)': {
            minHeight: 40,
          },
        },
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      select: {
        minHeight: '44px !important',
        display: 'flex',
        alignItems: 'center',
        '@media (min-width:768px)': {
          minHeight: '40px !important',
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        '@media (min-width:768px)': {
          borderRadius: 12,
        },
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        margin: 16,
        width: 'calc(100% - 32px)',
        maxWidth: '100%',
        '@media (min-width:768px)': {
          margin: 32,
          width: 'auto',
          maxWidth: 600,
        },
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        width: '100%',
        '@media (min-width:768px)': {
          width: 280,
        },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        padding: '8px 12px',
        '@media (min-width:768px)': {
          padding: '16px',
        },
      },
      head: {
        fontWeight: 600,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        height: 28,
        '@media (min-width:768px)': {
          height: 32,
        },
      },
    },
  },
  MuiContainer: {
    styleOverrides: {
      root: {
        paddingLeft: 16,
        paddingRight: 16,
        '@media (min-width:768px)': {
          paddingLeft: 24,
          paddingRight: 24,
        },
        '@media (min-width:1024px)': {
          paddingLeft: 32,
          paddingRight: 32,
        },
      },
    },
  },
};

// ============================================================
// Theme Creation
// ============================================================

/**
 * Create responsive theme with all configurations
 */
export function createResponsiveTheme(options?: ThemeOptions) {
  return createTheme({
    breakpoints,
    typography: responsiveTypography,
    components: responsiveComponents,
    palette: {
      mode: 'light',
      primary: {
        main: '#1976d2',
        light: '#42a5f5',
        dark: '#1565c0',
      },
      secondary: {
        main: '#dc004e',
        light: '#ff4081',
        dark: '#c51162',
      },
      background: {
        default: '#f5f5f5',
        paper: '#ffffff',
      },
    },
    shape: {
      borderRadius: 8,
    },
    ...options,
  });
}

// ============================================================
// Responsive Utilities
// ============================================================

/**
 * Grid column configuration by screen size
 */
export const gridColumns = {
  xs: 1,
  sm: 1,
  md: 2,
  lg: 3,
  xl: 4,
};

/**
 * Common responsive styles for containers
 */
export const responsiveContainerStyles = {
  px: { xs: 2, sm: 2, md: 3, lg: 4 },
  py: { xs: 2, sm: 2, md: 3, lg: 4 },
};

/**
 * Common responsive styles for cards in grid
 */
export const responsiveCardGridStyles = {
  display: 'grid',
  gap: { xs: 2, md: 3 },
  gridTemplateColumns: {
    xs: '1fr',
    sm: '1fr',
    md: 'repeat(2, 1fr)',
    lg: 'repeat(3, 1fr)',
    xl: 'repeat(4, 1fr)',
  },
};

/**
 * Hide on mobile, show on tablet+
 */
export const hideOnMobile = {
  display: { xs: 'none', md: 'block' },
};

/**
 * Show on mobile, hide on tablet+
 */
export const showOnMobile = {
  display: { xs: 'block', md: 'none' },
};

/**
 * Responsive flex direction (column on mobile, row on tablet+)
 */
export const responsiveFlexDirection = {
  flexDirection: { xs: 'column', md: 'row' },
};
